import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { EntityDrawer, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, TagInput } from "@fox1ck-org/ui";
import { Form } from "@fox1ck-org/ui/form";
import { toast } from "@fox1ck-org/ui/toast";
import type { AffiliateNetwork } from "@gen/tracker/v1/affiliate_network_pb";

import { RhfField } from "@/components/rhf-field";
import { MODEL_HINT, PAYOUT_MODELS, modelCode } from "@/components/offers/labels";
import { useT, type TKey } from "@/lib/i18n";
import {
  CURRENCIES,
  SILENCE_CHOICES,
  fieldErrorsOf,
  formToPartner,
  partnerField,
  partnerFormSchema,
  partnerToForm,
  type PartnerFormValues,
} from "@/lib/schemas/affiliate-network";
import { useCreateAffiliateNetwork, useUpdateAffiliateNetwork } from "@/lib/services/network-service";
import { showRefusal } from "@/lib/plan";

/**
 * The partner editor (board PartnerEditor), an EntityDrawer: Основное (name,
 * payout currency, default model, the cabinet's site), Защита (who may call), «Если постбеков нет» (the silence
 * alert, shown in «Здоровье данных»). Saving never changes the postback
 * address: the key is fixed at creation — «Обновить» from the board is not
 * offered, a new key would silence every cabinet that has the old one.
 * Field names as written (Label textCase="none"), the name a sans field; the
 * cabinet's address, the key and the IPs are data and stay mono.
 */

const CURRENCY_NAME: Partial<Record<string, TKey>> = {
  USD: "u6.cur.usd",
  EUR: "u6.cur.eur",
  RUB: "u6.cur.rub",
};

export function PartnerEditor({
  open,
  onClose,
  network,
  onSaved,
  onArchive,
}: {
  open: boolean;
  onClose: () => void;
  network?: AffiliateNetwork;
  onSaved: (n: AffiliateNetwork) => void;
  onArchive?: () => void;
}) {
  const t = useT();
  const initial = useMemo(() => partnerToForm(network), [network]);
  const form = useForm<PartnerFormValues>({ resolver: zodResolver(partnerFormSchema(t)), defaultValues: initial });
  const [rootError, setRootError] = useState<string | null>(null);
  const create = useCreateAffiliateNetwork();
  const update = useUpdateAffiliateNetwork();

  useEffect(() => {
    if (open) {
      form.reset(initial);
      setRootError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const save = form.handleSubmit(async (values) => {
    setRootError(null);
    const next = formToPartner(values, network);
    try {
      const saved = network ? await update.mutateAsync({ network: next }) : await create.mutateAsync(next);
      toast.success(t(network ? "u6.pe.saved" : "u6.pe.created"));
      onSaved(saved);
    } catch (err) {
      if (showRefusal(err)) return;
      const problems = fieldErrorsOf(err);
      if (problems.length === 0) {
        setRootError(err instanceof Error ? err.message : t("u6.ed.saveFailed"));
        return;
      }
      for (const p of problems) {
        const field = partnerField(p);
        if (field === "root") setRootError(p.message);
        else form.setError(field, { type: "server", message: p.message });
      }
    }
  });

  const c = form.control;
  const busy = form.formState.isSubmitting;

  return (
    <EntityDrawer
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={network ? t("u6.pe.title", { name: network.name }) : t("u6.pe.titleNew")}
      description={t("u6.pe.sub")}
      width="xl"
      primaryAction={{ label: t("u6.ed.save"), onClick: () => void save(), loading: busy, loadingLabel: t("u6.ed.saving") }}
      secondaryAction={{ label: t("u6.common.cancel"), onClick: onClose }}
      destructiveAction={onArchive ? { label: t("u6.common.archive"), onClick: onArchive } : undefined}
    >
      <Form {...form}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
          className="flex flex-col gap-4"
        >
          {rootError ? <p className="text-xs text-destructive">{rootError}</p> : null}
          <Sec n={1} title={t("u6.pe.sec.main")}>
            <RhfField control={c} name="name" label={t("u6.ed.name")} plain>
              {(f) => <Input {...f} autoFocus font="sans" />}
            </RhfField>
            <div className="grid grid-cols-2 gap-3">
              <RhfField control={c} name="currency" label={t("u6.pe.currency")} plain>
                {(f) => (
                  <Select value={f.value} onValueChange={f.onChange}>
                    <SelectTrigger aria-label={t("u6.pe.currency")}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((cur) => (
                        <SelectItem key={cur} value={cur}>
                          {CURRENCY_NAME[cur] ? `${cur} — ${t(CURRENCY_NAME[cur]!)}` : cur}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </RhfField>
              <RhfField control={c} name="defaultModel" label={t("u6.pe.model")} plain>
                {(f) => (
                  <Select value={f.value} onValueChange={f.onChange}>
                    <SelectTrigger aria-label={t("u6.pe.model")}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYOUT_MODELS.map((m) => (
                        <SelectItem key={m} value={String(m)}>
                          {MODEL_HINT[m] ? `${modelCode(m)} — ${t(MODEL_HINT[m]!)}` : modelCode(m)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </RhfField>
            </div>
            <RhfField control={c} name="cabinetUrl" label={t("u6.pe.cabinet")} description={t("u6.pe.cabinet.hint")} plain>
              {(f) => <Input {...f} placeholder="https://" spellCheck={false} />}
            </RhfField>
          </Sec>
          <Sec n={2} title={t("u6.pe.sec.security")}>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pe-key" textCase="none">
                  {t("u6.pe.key")}
                </Label>
                <Input id="pe-key" value={maskKey(network?.postbackSecurity?.token)} readOnly />
                <span className="text-xs text-muted-foreground">{t("u6.pe.key.hint")}</span>
              </div>
              <RhfField control={c} name="ipAllowlist" label={t("u6.pe.ips")} description={t("u6.pe.ips.hint")} plain>
                {(f) => <TagInput mode="values" mono value={f.value} onChange={f.onChange} placeholder={t("u6.pe.ips.any")} labels={{ input: t("u6.pe.ips"), remove: (v) => t("cond.removeValue", { v }) }} />}
              </RhfField>
            </div>
          </Sec>
          <Sec n={3} title={t("u6.pe.sec.silence")}>
            <RhfField control={c} name="silence" label={t("u6.pe.silence")} description={t("u6.pe.silence.hint")} plain>
              {(f) => (
                <Select value={f.value} onValueChange={f.onChange}>
                  <SelectTrigger aria-label={t("u6.pe.silence")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SILENCE_CHOICES.map((s) => (
                      <SelectItem key={s} value={String(s)}>
                        {silenceLabel(s, t)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </RhfField>
          </Sec>
        </form>
      </Form>
    </EntityDrawer>
  );
}

function Sec({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3 border-b border-border pb-4 last:border-0" aria-label={title}>
      <div className="flex items-center gap-2">
        <span className="text-xs tabular-nums text-muted-foreground">{n}</span>
        <h3 className="text-section-title">{title}</h3>
      </div>
      {children}
    </section>
  );
}

/** «k7f…a91»: enough to recognise the key, not enough to copy it. */
function maskKey(token: string | undefined): string {
  if (!token) return "—";
  return token.length > 8 ? `${token.slice(0, 3)}…${token.slice(-3)}` : "•••";
}

export function silenceLabel(seconds: number, t: ReturnType<typeof useT>): string {
  if (!seconds) return t("u6.pe.silence.never");
  if (seconds % 86400 === 0 && seconds >= 7 * 86400) return t("u6.pe.silence.days", { n: seconds / 86400 });
  return t("u6.pe.silence.hours", { n: seconds / 3600 });
}
